import { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { posts } from '@/mocks/posts';
import SiteLayout from '@/components/feature/SiteLayout';
import Breadcrumbs from '@/components/feature/Breadcrumbs';
import PostCard from '@/components/feature/PostCard';
import { useSeo } from '@/hooks/useSeo';
import { formatViews, getCategoryBySlug, site } from '@/utils/site';

export default function DetailPage() {
  const { id = '' } = useParams();
  const post = posts.find((item) => item.id === id);
  const [copied, setCopied] = useState(false);

  const related = useMemo(() => {
    if (!post) {
      return [];
    }
    const sameCategory = posts.filter(
      (item) => item.categorySlug === post.categorySlug && item.id !== post.id,
    );
    if (sameCategory.length >= 3) {
      return sameCategory.slice(0, 3);
    }
    const extra = posts.filter(
      (item) => item.categorySlug !== post.categorySlug && item.id !== post.id,
    );
    return [...sameCategory, ...extra].slice(0, 3);
  }, [post]);

  const hotList = useMemo(() => [...posts].sort((a, b) => b.views - a.views).slice(0, 5), []);

  const category = post ? getCategoryBySlug(post.categorySlug) : undefined;

  useSeo({
    title: post ? `${post.title}_黑料看点` : `内容未找到_${site.name}`,
    description: post
      ? `${post.excerpt.slice(0, 90)}… 黑料看点为你整理这段高能名场面的完整看点与背景。`
      : '该看点内容暂不存在，欢迎返回首页浏览其他合集。',
    keywords: post
      ? `黑料看点,${post.tags.join(',')},黑料看点精彩片段,黑料看点高能,黑料看点名场面合集`
      : site.keywords,
    path: `/detail/${id}`,
    jsonLd: post
      ? [
          {
            '@context': 'https://schema.org',
            '@type': 'Article',
            headline: post.title,
            description: post.excerpt,
            datePublished: post.date,
            articleSection: category?.name,
            author: { '@type': 'Organization', name: post.author },
            publisher: { '@type': 'Organization', name: site.name },
            keywords: post.tags.join(','),
          },
          {
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: '首页', item: '/' },
              {
                '@type': 'ListItem',
                position: 2,
                name: category?.name,
                item: `/category/${post.categorySlug}`,
              },
              { '@type': 'ListItem', position: 3, name: post.title },
            ],
          },
        ]
      : undefined,
  });

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  };

  if (!post) {
    return (
      <SiteLayout>
        <div className="max-w-3xl mx-auto px-4 md:px-8 pt-32 pb-24 text-center">
          <span className="mx-auto w-16 h-16 flex items-center justify-center rounded-full bg-background-100 text-foreground-400 text-3xl">
            <i className="ri-file-unknow-line" />
          </span>
          <h1 className="mt-6 font-heading text-3xl text-foreground-950">这个看点还没上线</h1>
          <p className="mt-3 text-sm text-foreground-600">内容可能已被调整，或链接有误。</p>
          <Link
            to="/"
            className="mt-6 inline-flex items-center gap-1.5 px-5 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors"
          >
            <i className="ri-arrow-left-line" />
            返回首页
          </Link>
        </div>
      </SiteLayout>
    );
  }

  return (
    <SiteLayout>
      <article className="w-full px-4 md:px-8 pt-24 md:pt-32 pb-12 md:pb-16">
        <div className="max-w-7xl mx-auto">
          <Breadcrumbs
            items={[
              { label: '首页', to: '/' },
              { label: category?.name || '分类', to: `/category/${post.categorySlug}` },
              { label: post.title },
            ]}
          />

          <header className="mt-6 max-w-3xl">
            <Link
              to={`/category/${post.categorySlug}`}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-primary-100 text-primary-800 text-xs font-medium hover:bg-primary-200 transition-colors"
            >
              <i className={category?.icon || 'ri-fire-line'} />
              {category?.name}
            </Link>
            <h1 className="mt-5 font-heading text-3xl md:text-5xl text-foreground-950 leading-tight">
              {post.title}
            </h1>
            <p className="mt-5 text-base md:text-lg text-foreground-600 leading-8">{post.excerpt}</p>

            <div className="mt-6 flex flex-wrap items-center gap-x-6 gap-y-3 text-xs md:text-sm text-foreground-500">
              <span className="flex items-center gap-1.5">
                <i className="ri-user-3-line" />
                {post.author}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-calendar-line" />
                {post.date}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-eye-line" />
                {formatViews(post.views)} 次围观
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-time-line" />
                阅读约 {post.readTime}
              </span>
            </div>
          </header>

          <div className="mt-8 w-full aspect-[16/9] md:aspect-[21/9] overflow-hidden rounded-lg bg-background-200">
            <img src={post.cover} alt={post.title} className="w-full h-full object-cover object-top" />
          </div>

          <div className="mt-10 md:mt-14 grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-14">
            <div className="lg:col-span-8">
              <div className="rounded-lg border border-accent-200 bg-accent-100 p-5 md:p-6">
                <h2 className="flex items-center gap-2 font-heading text-lg text-foreground-950">
                  <span className="w-7 h-7 flex items-center justify-center rounded-md bg-accent-500 text-foreground-950">
                    <i className="ri-flashlight-line" />
                  </span>
                  亮点速览
                </h2>
                <ul className="mt-4 space-y-3">
                  {post.highlights.map((item) => (
                    <li key={item} className="flex items-start gap-2.5 text-sm text-foreground-800 leading-7">
                      <i className="ri-checkbox-circle-fill text-accent-600 mt-1" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>

              <div className="mt-8 space-y-8">
                {post.sections.map((section, index) => (
                  <section key={section.heading} id={`section-${index + 1}`} className="scroll-mt-28">
                    <h2 className="font-heading text-xl md:text-2xl text-foreground-950 flex items-baseline gap-3">
                      <span className="text-primary-500 text-base">0{index + 1}</span>
                      {section.heading}
                    </h2>
                    <p className="mt-3 text-[15px] md:text-base text-foreground-700 leading-8">
                      {section.text}
                    </p>
                  </section>
                ))}
              </div>

              <div className="mt-10 pt-6 border-t border-background-200 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-xs text-foreground-500 mr-1">标签</span>
                  {post.tags.map((tag) => (
                    <span key={tag} className="px-3 py-1.5 rounded-full text-xs bg-secondary-100 text-secondary-900">
                      #{tag}
                    </span>
                  ))}
                </div>
                <button
                  type="button"
                  onClick={handleCopy}
                  className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-md border border-background-300 text-sm text-foreground-700 whitespace-nowrap hover:border-primary-400 hover:text-primary-700 transition-colors"
                >
                  <i className={copied ? 'ri-check-line' : 'ri-link'} />
                  {copied ? '链接已复制' : '复制本页链接'}
                </button>
              </div>
            </div>

            <aside className="lg:col-span-4 space-y-6">
              <div className="lg:sticky lg:top-24 space-y-6">
                <div className="bg-background-100 rounded-lg border border-background-200 p-5">
                  <h2 className="font-heading text-lg text-foreground-950 pb-4 border-b border-background-200">
                    本页目录
                  </h2>
                  <ol className="mt-4 space-y-2.5">
                    {post.sections.map((section, index) => (
                      <li key={section.heading}>
                        <a
                          href={`#section-${index + 1}`}
                          className="flex items-start gap-2.5 text-sm text-foreground-700 hover:text-primary-700 transition-colors"
                        >
                          <span className="mt-0.5 text-xs font-heading text-primary-500">
                            {String(index + 1).padStart(2, '0')}
                          </span>
                          <span className="leading-6">{section.heading}</span>
                        </a>
                      </li>
                    ))}
                  </ol>
                </div>

                <div className="bg-background-100 rounded-lg border border-background-200 p-5">
                  <h2 className="font-heading text-lg text-foreground-950 pb-4 border-b border-background-200">
                    大家都在看
                  </h2>
                  <ul className="mt-2">
                    {hotList.map((item, index) => (
                      <li key={item.id}>
                        <Link
                          to={`/detail/${item.id}`}
                          className="group flex items-start gap-3 py-3 border-b border-background-200 last:border-0"
                        >
                          <span
                            className={`w-6 h-6 shrink-0 flex items-center justify-center rounded-md font-heading text-xs ${
                              index < 3
                                ? 'bg-primary-500 text-background-50'
                                : 'bg-background-200 text-foreground-700'
                            }`}
                          >
                            {index + 1}
                          </span>
                          <span className="text-sm text-foreground-900 leading-snug line-clamp-2 group-hover:text-primary-700 transition-colors">
                            {item.title}
                          </span>
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </aside>
          </div>

          {related.length > 0 && (
            <section className="mt-14 md:mt-20 pt-10 border-t border-background-200">
              <div className="flex items-end justify-between gap-4 mb-8">
                <h2 className="font-heading text-2xl md:text-3xl text-foreground-950">相关看点推荐</h2>
                <Link
                  to={`/category/${post.categorySlug}`}
                  className="text-sm font-medium text-foreground-700 hover:text-primary-600 transition-colors whitespace-nowrap inline-flex items-center gap-1"
                >
                  更多{category?.name}
                  <i className="ri-arrow-right-line" />
                </Link>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
                {related.map((item) => (
                  <PostCard key={item.id} post={item} layout="grid" />
                ))}
              </div>
            </section>
          )}
        </div>
      </article>
    </SiteLayout>
  );
}